import { zodResolver } from '@hookform/resolvers/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Divider, FormLabel, MenuItem, Stack, TextField } from '@mui/material'
import { createTeamMember } from 'api/teams'
import { getUsersByAccessCode } from 'api/users'
import FormInput from 'components/form-elements/FormInput'
import FormMultipleSelect from 'components/form-elements/FormMultipleSelect'
import NotificationSys from 'components/NotificationSystem'
import { EMAIL_IS_INVALID, minButtonWidth, PHONE_IS_INVALID } from 'const'
import { useCharactersLeft } from 'hooks/useCharactersLeft'
import React, { ChangeEvent, useMemo, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'
import { Nullish, TeamMemberCreateRequest, traineeAllRoles, traineeRoleOptions } from 'types'
import { array, literal, object, string, TypeOf } from 'zod'
import { useTeams } from '../../hooks/useTeams'

const required = string().min(1, 'Required')
const optional = string().optional()

export function TimelineTeamMemberFormAdd({ onClose }: { onClose: () => void }) {
  const { activeTeamId, onRefetchTeamMembers } = useTeams()

  const membersQuery = useQuery('membersList', getUsersByAccessCode)

  const teamMembersOptions = useMemo(() => {
    const membersData = membersQuery.data?.data || []
    return membersData.map((member) => ({
      label: `${member.firstname} ${member.lastname} (${member.email})`,
      value: member.id,
    }))
  }, [membersQuery])

  const [activeMemberId, setActiveMemberId] = useState<Nullish<number>>(null)

  const hasActiveMember = useMemo(() => activeMemberId !== null, [activeMemberId])

  const formSchema = object({
    firstName: required,
    lastName: required,
    email: required.email(EMAIL_IS_INVALID),
    about: string().max(200, 'Max message length is 200 characters').optional(),
    role: array(
      object({
        label: string(),
        value: string(),
      }),
    ).optional(),
    otherRole: optional,
    addressStreet: optional,
    addressZipCode: optional,
    addressCountry: optional,
    addressCity: optional,
    addressState: optional,
    phone: string().min(10, PHONE_IS_INVALID).or(literal('')).optional(),
    comments: string(),
  })

  type FormType = TypeOf<typeof formSchema>

  const methods = useForm<FormType>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      about: '',
      role: [],
      addressStreet: '',
      addressZipCode: '',
      addressCountry: '',
      addressCity: '',
      addressState: '',
      phone: '',
      comments: '',
    },
  })
  const { handleSubmit, watch, setValue } = methods

  const createMutation = useMutation(createTeamMember, {
    onSuccess: async () => {
      NotificationSys.showSuccess('Team Member successfully created')
      onClose()
      await onRefetchTeamMembers()
    },
  })

  const onSubmitHandler: SubmitHandler<FormType> = (values: FormType) => {
    if (!activeTeamId) {
      NotificationSys.showWarning('No active team selected')
      return
    }

    const role = values.role?.map((role) => role.value) || []
    const otherRole = hasOtherRole ? values.otherRole : ''

    const request: TeamMemberCreateRequest = {
      id: activeTeamId,
      userId: activeMemberId || 0,
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      about: values.about || '',
      role,
      otherRole,
      addressStreet: values.addressStreet || '',
      addressZipCode: values.addressZipCode || '',
      addressCountry: values.addressCountry || '',
      addressCity: values.addressCity || '',
      addressState: values.addressState || '',
      phone: values.phone || '',
      comments: values.comments || '',
    }

    createMutation.mutate(request)
  }

  const aboutMeValue = watch('about')
  const charactersLeft = useCharactersLeft(200, aboutMeValue?.length || 0)

  const roleValue = watch('role')
  const hasOtherRole = useMemo(() => {
    return roleValue?.some((role) => role.value === 'other')
  }, [roleValue])

  const handleChangeMember = (event: ChangeEvent<HTMLInputElement>) => {
    const newActiveMemberId = event.target.value ? parseInt(event.target.value, 10) : null
    setActiveMemberId(newActiveMemberId)
    if (newActiveMemberId) {
      const membersData = membersQuery.data?.data
      if (!membersData) return

      const selectedMember = membersData.find((member) => member.id === newActiveMemberId)
      if (selectedMember) {
        setValue('firstName', selectedMember.firstname || '')
        setValue('lastName', selectedMember.lastname || '')
        setValue('email', selectedMember.email || '')
        setValue('about', selectedMember.about || '')

        const role = selectedMember.role.map((roleValue) => {
          const option = traineeAllRoles.find((optionValue) => optionValue.value === roleValue) || {
            label: roleValue,
            value: roleValue,
          }
          return { label: option.label, value: option.value }
        })
        setValue('role', role)
      }
    } else {
      setValue('firstName', '')
      setValue('lastName', '')
      setValue('email', '')
      setValue('about', '')
      setValue('role', [])
    }
  }

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(onSubmitHandler)} width="100%" noValidate>
        <Stack spacing={1.5} mb={2}>
          <FormLabel>Select Member</FormLabel>
          <TextField select size="small" onChange={handleChangeMember} value={activeMemberId || ''}>
            <MenuItem value="">None</MenuItem>
            {teamMembersOptions.map((option) => (
              <MenuItem key={String(option.value)} value={String(option.value)}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Divider>OR</Divider>

          <FormInput label="First Name" name="firstName" fullWidth disabled={hasActiveMember} />

          <FormInput label="Last Name" name="lastName" fullWidth disabled={hasActiveMember} />

          <FormInput label="Email" name="email" fullWidth disabled={hasActiveMember} />

          <FormMultipleSelect
            name="role"
            label="Role"
            options={activeMemberId ? traineeRoleOptions : traineeAllRoles}
            defaultValue={[]}
            isLoading={false}
          />

          {hasOtherRole && <FormInput name="otherRole" label="Other Role" fullWidth />}

          <FormInput label="Phone Number" name="phone" fullWidth />

          <FormInput label="Street Address" name="addressStreet" fullWidth />
          <FormInput label="Zip Code" name="addressZipCode" fullWidth />
          <FormInput label="City" name="addressCity" fullWidth />
          <FormInput label="State" name="addressState" fullWidth />
          <FormInput label="Country" name="addressCountry" fullWidth />

          <FormInput
            name="about"
            label="What you might want to know about me"
            placeholder="Please enter the relevant information about you"
            fullWidth
            minRows={3}
            maxRows={6}
            multiline
            helperText={charactersLeft}
            checkTouchField={false}
            disabled={hasActiveMember}
          />

          <FormInput label="Comments" name="comments" fullWidth multiline />
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button variant="outlined" onClick={onClose} sx={minButtonWidth}>
            Close
          </Button>
          <LoadingButton loading={false} variant="contained" type="submit" sx={minButtonWidth}>
            Save
          </LoadingButton>
        </Stack>
      </Box>
    </FormProvider>
  )
}
